import React, { useCallback, useState } from 'react'
import Fuse from 'fuse.js'
import { v4 as uuid } from 'uuid'
import { useQuery } from '@tanstack/react-query'
import { SpinnerWrapper } from '../../common/SpinnerWrapper'
import { ErrorView } from '../../../views/error/ErrorView'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { IllustrationChoices } from '../../common/Illustration'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { AccordionSection } from '../../common/AccordionSection'
import { RegisterEntranceItem } from '../RegisterEntranceItem/RegisterEntranceItem'
import { SearchBar } from '../../common/SearchBar'
import { entranceQueries } from '../../../api/society-entrances'
import { sortStringsAlphabetically } from '../../../helpers/sorting'
import GettingStarted from '../GettingStarted/GettingStarted'
import { i18nSocietyType } from '../../../i18n'

export const RegisterEntranceList = (): React.ReactNode => {
  const [searchString, setSearchString] = useState('')
  const { translate } = useAppTranslation()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()
  const { data: entrances, isLoading: isLoadingEntrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )

  const loading = isLoadingSociety || isLoadingEntrances

  const getFilteredEntrances = useCallback(() => {
    const options = {
      threshold: 0.0,
      keys: ['addressStreet'],
    }
    const _entrances = entrances || []
    _entrances.sort((a, b) =>
      sortStringsAlphabetically(a.addressStreet, b.addressStreet)
    )

    const fuse = new Fuse(_entrances, options)

    const filteredEntrances =
      searchString === ''
        ? _entrances
        : fuse.search(searchString).map((result) => result.item)

    return filteredEntrances
  }, [entrances, searchString])

  const onSearchBarChange = (e: React.FormEvent<HTMLInputElement>): void => {
    setSearchString(e.currentTarget.value)
  }

  if (loading) {
    return <SpinnerWrapper />
  }

  if (!loading && entrances && entrances.length === 0) {
    return (
      <ErrorView
        title={translate('common.emptyState.title')}
        subtitle={translate('common.emptyState.subtitle')}
        illustration={IllustrationChoices.EMPTY}
      />
    )
  }

  const _filteredEntrances = getFilteredEntrances()

  return (
    <div className="w-full space-y-4">
      <GettingStarted
        title={translate('gettingStartedScreen.screens.register.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        description={translate(
          'gettingStartedScreen.screens.register.description',
          { context: i18nSocietyType(society?.societyTypeDisplayName) }
        )}
        gettingStartedId="register"
      />
      <SearchBar
        className="w-full md:w-80"
        widthClassName="flex grow"
        value={searchString}
        onChange={onSearchBarChange}
      />
      {_filteredEntrances.length > 0 && (
        <AccordionSection
          items={_filteredEntrances.map((_entrance) => ({
            id: uuid(),
            title: `${_entrance.addressStreet}`,
            content: (
              <RegisterEntranceItem key={_entrance._id} entrance={_entrance} />
            ),
          }))}
        />
      )}
    </div>
  )
}
